import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { withRouter } from 'react-router-dom';

import { authContext } from "EstruturaAntiga/adalConfig";
import Error401 from "EstruturaAntiga/pages/errorPages/401";
import PaginaErroNaoTratado from "EstruturaAntiga/pages/errorPages/paginaErroNaoTratado";
import Loading from "EstruturaAntiga/components/loading/Loading";
import Error404 from "EstruturaAntiga/pages/errorPages/404";
import Error503 from "EstruturaAntiga/pages/errorPages/503";

import AuthenticationService from "./AuthenticationService";
import CONSTANTES_AMBIENTE from 'constants/constantesAmbiente';
import TermosAceiteService from "services/TermosAceiteService";
import { NotificationManager } from "react-notifications";
import { MessagesDefaultTimeout } from "constants/messagesDefaultTimeout";
import { escapeRegex, guidRegexStr } from "utils/regex";
import {
    direitoPertenceAoContexto,
    ehMesmoContexto,
    escolherContextoInicial,
    escolherDireitoDaRota,
    listarDireitosPermitidosNaRota,
    montarDireitoDoContexto,
    montarTenantsDosContextos,
    navegacaoPermiteDireito
} from "utils/contextos";

const AuthContext = createContext();

export const AuthProvider = ({children, history}) => {
    const [user, setUser] = useState(null);
    const [contextos, setContextos] = useState([]);
    const [contexto, setContexto] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [contato, setContato] = useState(null);
    const [direito, setDireito] = useState(null);
    const [navegacoes, setNavegacoes] = useState([]);
    const [termosAceite, setTermosAceite] = useState([]);
    const [authorized, setAuthorized] = useState(false);    
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ehUsuarioGrupoBhs, setEhUsuarioGrupoBhs] = useState(false);
    const tenants = useMemo(() => montarTenantsDosContextos(contextos), [contextos]);

    useEffect(()=>{
        Initialization();
    },[])

    const Initialization = async () => {
        const lastUrl = sessionStorage.getItem('lastUrl');
        if(!lastUrl) {
            sessionStorage.setItem("lastUrl", window.location.pathname + window.location.search);
        }
        authContextInstance.forceLogout = forceLogout;
        const cachedUser = ObterUsuarioAdEmCache();
        if(cachedUser){
            let [sucesso, authData, error] = await AuthenticationService.AtualizarEmCacheDadosRelacionadosAoToken();
            if(!sucesso){
                setLoading(false);
                return;
            }

            setUser(cachedUser)

            if (cachedUser?.userName) {
                const dominios = Object.values(CONSTANTES_AMBIENTE.DOMINIOS_GRUPO_BHS);                
                setEhUsuarioGrupoBhs(
                    dominios.some(domain =>
                        cachedUser.userName.toLowerCase().includes(domain)
                    )
                );
            } else {
                setEhUsuarioGrupoBhs(false);
            }

            sucesso = await AtualizarDadosRelacionadosAoUsuario(authData);
            setLoading(false);
            
            if(!sucesso){
                return;
            }
            setAuthorized(true);
            AtualizarContatoComDadosPerfil(cachedUser);
        }
        else{
            setAuthorized(false)
            setLoading(false)
            clearCache();
            history.push("/login")
        }
    }
    const AtualizarDadosRelacionadosAoUsuario = async (authData) =>{
        const [contatoNovoSucesso, contatoNovo, contatoNovoError] = await AuthenticationService.AtualizarEmCacheContatoDoUsuario(authData);
        if(!contatoNovoSucesso){
            setError(contatoNovoError);
            return false;
        }
        setContato(contatoNovo);

        const [listaContextosSucesso, listaContextos, listaContextosError] = await AuthenticationService.AtualizarEmCacheContextosDoUsuario();
        if(!listaContextosSucesso){
            setError(listaContextosError);
            return false;
        }
        if(listaContextos.length == 0){
            setError({statuscode: 404, msg: "Assinaturas do usuário não encontradas."});
            return false;
        }
        setContextos(listaContextos)

        const contextoEmCache = AuthenticationService.ObterContextoSelecionadoEmCache();
        const contextoAtual = escolherContextoInicial(listaContextos, contextoEmCache);
        const contextoDaAbaMantido = ehMesmoContexto(contextoAtual, contextoEmCache);
        AuthenticationService.SalvarContextoSelecionadoEmCache(contextoAtual);
        setContexto(contextoAtual)

        const direitoEmCache = JSON.parse(sessionStorage.getItem("signature"));
        const direitoAtual = contextoDaAbaMantido && direitoPertenceAoContexto(contextoAtual, direitoEmCache?.entitlementId)
            ? direitoEmCache
            : montarDireitoDoContexto(contextoAtual, contextoAtual.direitos[0]);
        sessionStorage.setItem("signature", JSON.stringify(direitoAtual));
        setDireito(direitoAtual)

        const [tenantAtualSucesso, tenantAtual, tenantAtualError] = await AuthenticationService.AtualizarEmCacheTenantAtual(contextoAtual.tenantId, !contextoDaAbaMantido);
        if(!tenantAtualSucesso){
            setError(tenantAtualError);
            return false;
        }
        setTenant(tenantAtual)

        const [navegacoesSucesso, listaNavegacoes, navegacoesError] = await AuthenticationService.AtualizarEmCacheNavegacoesDoUsuario();
        if(!navegacoesSucesso){
            setError(navegacoesError);
            return false;
        }
        setNavegacoes(listaNavegacoes)

        const [termosAceiteSucesso, listaTermosAceite, termosAceiteError] = await AuthenticationService.AtualizarEmCacheTermosAceite();
        if(!termosAceiteSucesso){
            setError(termosAceiteError);
            return false;
        }
        setTermosAceite(listaTermosAceite)

        return true;
    }
    const AtualizarContatoComDadosPerfil = async (userData) =>{
        let [sucesso, novoContato, error] = await AuthenticationService.AtualizarEmCacheContatoComPerfilDoUsuario(userData)
        if(!sucesso){
            return false;
        }
        setContato(novoContato);
        return true;
    }

    const ObterUsuarioAdEmCache = ()=>{
        let cachedUser = authContext.getCachedUser();
        if(!cachedUser){
            authContext.handleWindowCallback();
            cachedUser = authContext.getCachedUser();
        }
        
        return cachedUser
    }

    const logout = () =>{
        authContext.clearCache();
        clearCache();
        setUser(null)
        setAuthorized(false)
        authContext.logOut()
    }
    const clearCache = () =>{
        const lastUrl = sessionStorage.getItem('lastUrl');
        sessionStorage.clear();
        localStorage.clear();
        if (lastUrl) {
            sessionStorage.setItem("lastUrl", lastUrl);
        }
    }

    const forceLogout = () => {
        logout();
    };

    const login =() =>{
        authContext.login()
    }

    const ObterParametrosRelatorio = (pathname) => {
        let reportID = pathname.split("/relatorio/")[1]
        let relatorio = navegacoes.find(navegacao => navegacao.relatorioPowerBI && navegacao.reportID == reportID);
        if(!relatorio) return {};
        return {
            Key: contato.idContact,
            groupID: relatorio.groupID,
            reportID: relatorio.reportID,
            datasetID: relatorio.datasetID,
            chaveFiltroRLS: relatorio.chaveFiltroRLS
        };
    }
    
    const ObterNavegacaoPeloCaminho = (pathname) => {
        let reportID = pathname.split("/relatorio/")[1]
        if(reportID){
            let relatorio = navegacoes.find(navegacao => navegacao.relatorioPowerBI && navegacao.reportID == reportID);
            return relatorio
        }else{
            let navegacao = navegacoes.find(navegacao => navegacao.caminho == pathname)
            return navegacao;
        }
    }
    const pathToRegex = (pathPattern) => {
        const escaped = escapeRegex(pathPattern);
        const withGuid = escaped.replace(/:id\b/g, `(${guidRegexStr})`);
        return new RegExp(`^${withGuid}$`);
    };

    const ListarNavegacoesDaRota = (caminhoAtual, relatorioParams = {}) => {
        return navegacoes.filter(nav=>{
            if(caminhoAtual.startsWith("/relatorio")){
                return nav.caminho == "/relatorio"
            }
            
            if(nav.caminho == caminhoAtual) return true;

            if (nav.caminho.endsWith("/:id")) {
                const re = pathToRegex(nav.caminho);
                return re.test(caminhoAtual);
            }
        }).filter(nav => validaParametrosRelatorio(nav, relatorioParams));
    }

    const ValidaPermissaoAcessoRota = (caminhoAtual, relatorioParams = {}) => {
        if(!contexto) return false;
        const navegacoesDaRota = ListarNavegacoesDaRota(caminhoAtual, relatorioParams);

        return navegacoesDaRota.some(nav =>
            contexto.direitos.some(direitoDoContexto => navegacaoPermiteDireito(nav, direitoDoContexto.direitoId)));
    }

    const RotaPermitidaParaMaisDeUmDireito = (caminhoAtual, relatorioParams = {}) => {
        if(!contexto) return false;
        return listarDireitosPermitidosNaRota(contexto, ListarNavegacoesDaRota(caminhoAtual, relatorioParams)).length > 1;
    }

    const DefinirDireitoDaRota = (caminhoAtual, relatorioParams = {}) => {
        if(!contexto) return;
        const navegacoesDaRota = ListarNavegacoesDaRota(caminhoAtual, relatorioParams);
        const direitoDaRota = escolherDireitoDaRota(contexto, navegacoesDaRota, direito?.entitlementId);
        if(direitoDaRota.direitoId == direito?.entitlementId) return;

        const novoDireito = montarDireitoDoContexto(contexto, direitoDaRota);
        sessionStorage.setItem("signature", JSON.stringify(novoDireito));
        setDireito(novoDireito);
    }

    function validaParametrosRelatorio(nav, params) {
        if (!nav.relatorioPowerBI) return true;
        const camposObrigatorios = ['groupID', 'reportID', 'datasetID', 'chaveFiltroRLS'];

        const camposValidos = camposObrigatorios.every(field => {
            return params?.[field] != null;
        });

        if (!camposValidos) return false;

        return (
            nav.groupID === params.groupID &&
            nav.reportID === params.reportID &&
            nav.datasetID === params.datasetID &&
            nav.chaveFiltroRLS === params.chaveFiltroRLS
        );
    }

    const AceitarTermoAceite = (id) =>{
        let data = {
            "idContato": contato.idContact,
            "idTermoAceite": id,
            "device": navigator.userAgent,
            "ipAddress": user.profile.ipaddr,
            "timeApproval": new Date()
        }

        let sucesso = TermosAceiteService.AceitarTermoAceite(data)
        .then(async (response)=>{
            let termosPendentes = termosAceite.filter(termo=>termo.id != id);
            setTermosAceite(termosPendentes)
            const [sucesso, termosAceiteResponse, error] = await AuthenticationService.AtualizarEmCacheTermosAceite(true);
            NotificationManager.success('Termo aceito com sucesso.', 'Sucesso!', MessagesDefaultTimeout.Success); 
            return true
        })
        .catch(error=>{
            NotificationManager.error('Ocorreu um erro ao aceitar o Termo.', 'Error!', MessagesDefaultTimeout.Error); 
            return false
        });

        return sucesso;
    }

    const AlterarContexto = async (novoContexto) =>{
        setLoading(true);

        let [tenantSucesso, tenantResponse, tenantError] = await AuthenticationService.AtualizarEmCacheTenantAtual(novoContexto.tenantId, true)
        if(!tenantSucesso){
            setLoading(false);
            setError({statuscode: tenantError?.statuscode || 500, msg: "Erro ao atualizar o Tenant Atual"})
            return;
        }

        const novoDireito = montarDireitoDoContexto(novoContexto, novoContexto.direitos[0]);
        sessionStorage.setItem("signature", JSON.stringify(novoDireito));
        AuthenticationService.SalvarContextoSelecionadoEmCache(novoContexto);

        setContexto(novoContexto);
        setDireito(novoDireito);
        setTenant(tenantResponse);
        setLoading(false);
        history.push("/dashboard")
    }

    return(
        <AuthContext.Provider value={
            {
                login, 
                logout,
                AlterarContexto,
                Initialization,
                ValidaPermissaoAcessoRota,
                DefinirDireitoDaRota,
                RotaPermitidaParaMaisDeUmDireito,
                AceitarTermoAceite,
                ObterParametrosRelatorio,
                ObterNavegacaoPeloCaminho,
                user, 
                loading, 
                contextos,
                contexto,
                tenants, 
                tenant, 
                contato, 
                direito,
                navegacoes,
                termosAceite,
                authorized,
                ehUsuarioGrupoBhs
            }}>
                {loading ? 
                    <Loading/> :
                        error != null ? 
                            error?.statuscode == 401 ? <Error401/> :
                            error?.statuscode == 503 ? <Error503/> :
                            <PaginaErroNaoTratado/>
                        : children
                }
        </AuthContext.Provider>
    )
}
export const useAuth = () => withRouter(useContext(AuthContext));

export const authContextInstance = {
    forceLogout: () => {
        throw new Error("AuthContext não foi inicializado corretamente!");
    },
};
